import { Page, EntityName, Link, CardTable } from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import {
  useTableProps,
  CurrencyValue,
  WalletAddress,
  CryptoIcon,
  TableSkeleton,
  Heading
} from '@blockanalitica/ui';
import { useApi, PaginatedResponse } from '@/api';
import { useParams } from 'react-router';
import { useNetwork } from '@/hooks';

type SuppliersData = {
  wallet_address: string;
  underlying_symbol: string;
  balance: string | null;
  order_index: string;
};

interface SuppliersResponse extends PaginatedResponse<SuppliersData[]> {
  extra: {
    underlying_symbol: string;
    address: string;
    name: string;
  };
}

export default function SuppliersTable() {
  const network = useNetwork();
  const { marketAddress, collateralAddress } = useParams();
  const { pagination, sorting } = useTableProps({
    pageSize: 20,
    initialSorting: '-balance'
  });

  const [data, error] = useApi<SuppliersResponse>({
    url: `/markets/${marketAddress}/collaterals/${collateralAddress}/wallets/`,
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} />;
  }
  const columns: TableColumn<SuppliersData>[] = [
    {
      header: 'Wallet Address',
      getCell: (row) => <WalletAddress address={row.wallet_address} />
    },
    {
      header: 'Amount',
      getCell: (row) => (
        <CurrencyValue
          value={row.balance}
          options={{
            currency: {
              name: row.underlying_symbol,
              address: data.extra.address,
              network: network
            }
          }}
        />
      ),
      sortingField: 'balance',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <Page
      title={
        <Heading
          display="flex"
          flexDirection="row"
          gap="2xs"
          alignItems="center">
          <EntityName
            entitySize="4"
            symbol1={
              <CryptoIcon
                name={data.extra.underlying_symbol}
                address={data.extra.address}
                network={network}
                size="4"
              />
            }>
            {data.extra.name}
          </EntityName>
          Wallets
        </Heading>
      }>
      <CardTable<SuppliersData>
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.wallet_address}
        rowLinkProps={{
          to: (row) =>
            `/${network}/${marketAddress}/borrowers/${row.wallet_address}`,
          // @ts-ignore
          linkComponent: Link
        }}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.pagination.total
        }}
      />
    </Page>
  );
}
